import type { PlaybackTransport } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import { RefreshIcon } from "@/components/icons";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import type { LoadState } from "../loadState";
import type { CollectionBandState, EpisodePhaseGridResult } from "./diagnosticsSeries";
import { episodeGridStatusText } from "./diagnosticsStatusText";
import { EpisodeOverviewCard } from "./EpisodeOverviewCard";
import { EpisodeParquetDeviationVisual } from "./EpisodeParquetDeviationVisual";
import type { ChannelStructureIndex } from "./parquetChannels";
import { secondsToPhase, type PhaseSpan } from "./segments/segmentShading";
import { usePlaybackCssVar } from "./segments/usePlaybackCssVar";
import type { EpisodeDiagnosticsState } from "./useEpisodeDiagnostics";
import styles from "./EpisodeDiagnosticsSurface.module.css";

/**
 * 채널 밴드 보기. 기준 밴드(p10–p90 통로) 위에 이 에피소드의 min–max 밴드와 mean 선을 겹친다.
 * 기준 밖 비율은 색으로 강조한다. 밴드가 없거나 채널이 밴드에 없으면 에피소드 시리즈만
 * 중립색으로 그린다.
 *
 * 이 보기는 재생 위치와 구간 음영도 겹쳐 그린다. 두 가로축은 서로 다르다. 그래프 축은 행 위상이고
 * 영상 축은 시간이다. 그래서 시간을 길이로 나눠 위상으로 옮기며, 이 값은 근사다.
 * 행 간격이 고르면 거의 맞는다. 재생선은 CSS 변수 하나로만 움직인다.
 * 그래서 sparkline 수백 개를 다시 그리지 않는다.
 *
 * 위상 그리드는 진단 상세의 두 번째 RTT 로 온다. 그래서 이 보기만 그리드를 기다린다.
 */
export function ChannelBandsView({
  episodeUuid,
  grid,
  band,
  episodeDiagnostics,
  channels,
  onSeekToPhase,
  onRefresh,
  shading = [],
  transport = null,
  durationSeconds = null,
}: {
  episodeUuid: string;
  grid: LoadState<EpisodePhaseGridResult>;
  band: CollectionBandState | null;
  episodeDiagnostics: EpisodeDiagnosticsState | null;
  channels: ChannelStructureIndex;
  onSeekToPhase?: (phase: number) => void;
  // 진단 상세와 위상 그리드를 처음부터 다시 받는다.
  onRefresh: () => void;
  shading?: readonly PhaseSpan[];
  transport?: PlaybackTransport | null;
  // 영상 길이. 재생 시각을 가로축 값으로 옮기는 데 쓴다. 길이를 모르면 재생선을 긋지 않는다.
  durationSeconds?: number | null;
}): ReactNode {
  // 그리드가 오기 전에는 그래프가 없다. 그래서 그래프가 붙는 순간을 state 로 잡는다.
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  usePlaybackCssVar(root, transport, "--diag-playhead-phase", (snapshot) =>
    secondsToPhase(snapshot.currentTimeSeconds, durationSeconds),
  );

  const refreshAction = { label: "새로고침", onClick: onRefresh, icon: <RefreshIcon /> };
  const retryAction = { label: "다시 시도", onClick: onRefresh, icon: <RefreshIcon /> };
  if (grid.status === "idle" || grid.status === "loading") {
    return <LoadingState>Parquet 미리보기를 불러오고 있습니다.</LoadingState>;
  }
  if (grid.status === "error") {
    return (
      <EmptyState tone="error" action={retryAction}>
        {grid.message}
      </EmptyState>
    );
  }
  const result = grid.data;
  // `declined` 는 오지 않는다. 에피소드 진단은 밴드 집계 플래그와 관계없이 항상 실행된다.
  // 행이 없으면 상태는 `not_computed` 다. 그래서 "아직 산출되지 않았습니다"가 맞는 문구다.
  if (result.kind === "status") {
    return <EmptyState action={refreshAction}>{episodeGridStatusText(result)}</EmptyState>;
  }
  if (result.kind === "unavailable") {
    return (
      <EmptyState tone="error" action={retryAction}>
        진단 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
      </EmptyState>
    );
  }
  if (result.grid.channels.size === 0) {
    return <EmptyState>표시할 진단 채널이 없습니다.</EmptyState>;
  }
  return (
    <div ref={setRoot} className={styles.viewBody}>
      <EpisodeParquetDeviationVisual
        grid={result.grid}
        band={band?.band ?? null}
        bandStatus={band?.status ?? null}
        bandComputedAt={band?.computedAt ?? null}
        channels={channels}
        onSeekToPhase={onSeekToPhase}
        shading={shading}
        showPlayhead={transport !== null && durationSeconds !== null}
        leadingCard={
          <EpisodeOverviewCard
            episodeUuid={episodeUuid}
            episodeDiagnostics={episodeDiagnostics}
            band={band?.band ?? null}
          />
        }
      />
    </div>
  );
}
